import React from "react";
import { useAppSelector } from "../../../hooks/hooks";

// redux getters
import {
  selectPropertyReport,
  selectPropertyReportStatus,
} from "../../../features/reports/reportsSlice";

// utils
import {
  generateList,
  formatNumberCommas,
  formatNumberRemoveCommas,
  parseDollarStringToNumber,
  parseDate,
  generateLivingSpacePhrase,
  generateLayoutPhrase,
  generateLotSizePhrase,
  generateConstructionPhrase,
  calculatePercentageChange,
} from "../../../utils";

// components
import {
  Container,
  Layout,
  Padding,
  Text,
  CollapsableItemList,
  ContainerLineItem,
  Table,
  Icon,
  IconList,
  LoadingPlaceholder,
} from "@kidslivesafe/sc-react";

import StreetView from "@kidslivesafe/sc-react/lib/atoms/StreetView";

import LoadingContainerLineItem from "../../../components/UIComponents/LoadingContainerLineItem/LoadingContainerLineItem";
import { selectBrandInfo } from "../../../features/brand/brandSlice";

const LotBuildingDetails: React.FC<{
  containerId: string;
  printReady?: boolean;
}> = ({ containerId, printReady }) => {
  const pulledReport = useAppSelector(selectPropertyReport);
  const propertyReportStatus = useAppSelector(selectPropertyReportStatus);
  const brandInfo = useAppSelector(selectBrandInfo);

  const loading = propertyReportStatus === "loading";

  /* generate lotObject */
  const highlightObjects = {
    ...{
      "Value Appreciation": (
        <>
          <Text size="sm" fontWeight="bold">
            {pulledReport?.propertyValuation?.latest?.valuationAmount
              ?.marketValue
              ? `$${formatNumberCommas(
                  pulledReport.propertyValuation.latest.valuationAmount
                    .marketValue,
                )} ${pulledReport.propertyCurrentOwnership?.saleAmount ? `(${Number(pulledReport.propertyValuation.latest.valuationAmount.marketValue) > parseDollarStringToNumber(pulledReport.propertyCurrentOwnership.saleAmount) ? `+` : "-"}${calculatePercentageChange(parseDollarStringToNumber(pulledReport.propertyCurrentOwnership.saleAmount), Number(pulledReport.propertyValuation.latest.valuationAmount.marketValue))}%)` : ""}`
              : "-"}
          </Text>
          <Text size="xs" tag="i">
            {pulledReport?.propertyCurrentOwnership?.saleAmount &&
            pulledReport.propertyCurrentOwnership.saleDate
              ? `Last sold ${parseDate(pulledReport.propertyCurrentOwnership.saleDate, "month-year")} for ${pulledReport.propertyCurrentOwnership.saleAmount}`
              : pulledReport?.propertyCurrentOwnership?.saleDate &&
                  !pulledReport?.propertyCurrentOwnership?.saleDate
                ? `Sale ${parseDate(pulledReport.propertyCurrentOwnership.saleDate, "month-year")}, value not disclosed`
                : "Last sale not disclosed"}
          </Text>
        </>
      ),
    },
    ...{
      "Living Space": (
        <>
          <Text size="sm" fontWeight="bold">
            {pulledReport?.propertyDetails?.buildingSquareFeet
              ? `${pulledReport.propertyDetails.buildingSquareFeet} sq ft ${
                  pulledReport?.blockDetails?.medianSumSqft
                    ? `(${
                        formatNumberRemoveCommas(
                          pulledReport.propertyDetails.buildingSquareFeet,
                        ) > Number(pulledReport.blockDetails.medianSumSqft)
                          ? `+`
                          : formatNumberRemoveCommas(
                                pulledReport.propertyDetails.buildingSquareFeet,
                              ) ===
                              Number(pulledReport.blockDetails.medianSumSqft)
                            ? ""
                            : "-"
                      }${calculatePercentageChange(
                        formatNumberRemoveCommas(
                          pulledReport.propertyDetails.buildingSquareFeet,
                        ),
                        Number(pulledReport.blockDetails.medianSumSqft),
                      )}%)`
                    : ""
                }`
              : "-"}
          </Text>
          <Text size="xs" tag="i">
            {generateLivingSpacePhrase(pulledReport)}
          </Text>
        </>
      ),
    },
    ...{
      Layout: (
        <>
          <Text size="sm" fontWeight="bold">
            {pulledReport?.propertyDetails?.bedrooms &&
            pulledReport?.propertyDetails?.totalBaths
              ? `${pulledReport.propertyDetails.bedrooms} bd / ${pulledReport.propertyDetails.totalBaths} ba`
              : "-"}
          </Text>
          <Text size="xs" tag="i">
            {generateLayoutPhrase(pulledReport)}
          </Text>
        </>
      ),
    },
    ...{
      "Lot Size": (
        <>
          <Text size="sm" fontWeight="bold">
            {pulledReport?.propertyDetails?.landSquareFootage &&
            formatNumberRemoveCommas(
              pulledReport.propertyDetails.landSquareFootage,
            ) < 10680
              ? `${pulledReport.propertyDetails.landSquareFootage} sq ft`
              : pulledReport?.propertyDetails?.acres
                ? `${pulledReport.propertyDetails.acres} acres`
                : "-"}
          </Text>
          <Text size="xs" tag="i">
            {generateLotSizePhrase(pulledReport)}
          </Text>
        </>
      ),
    },
    ...{
      Construction: (
        <>
          <Text size="sm" fontWeight="bold">
            {pulledReport?.propertyDetails?.yearBuilt
              ? `Built in ${pulledReport.propertyDetails.yearBuilt}`
              : `-`}
          </Text>
          <Text size="xs" tag="i">
            {generateConstructionPhrase(pulledReport)}
          </Text>
        </>
      ),
    },
  };

  /* generate lotObject */
  const lotObjects = [
    {
      ...(pulledReport?.propertyReportSummary?.universalLandUse && {
        "Land Use": pulledReport?.propertyReportSummary?.universalLandUse,
      }),
      ...(pulledReport?.propertyDetails?.apn && {
        "Assessors Parcel Number": pulledReport?.propertyDetails?.apn,
      }),
      ...(pulledReport?.propertyDetails?.acres &&
        pulledReport?.propertyDetails?.landSquareFootage && {
          "Lot Size": `${pulledReport?.propertyDetails?.acres} acres (${pulledReport?.propertyDetails?.landSquareFootage} sq ft)`,
        }),
      ...(pulledReport?.propertyDetails?.frontFootage &&
        pulledReport?.propertyDetails?.depthFootage && {
          "Recorded Lot Dimensions": `${pulledReport?.propertyDetails?.frontFootage} ft (front) / ${pulledReport?.propertyDetails?.depthFootage} ft (depth)`,
        }),
    },
  ];

  /* generate buildingObject */
  const buildingObjects = [
    {
      ...(pulledReport?.propertyDetails?.buildingSquareFeet && {
        "Total Square Footage": `${pulledReport?.propertyDetails?.buildingSquareFeet} sq ft`,
      }),
      ...(pulledReport?.propertyDetails?.bedrooms && {
        Bedrooms: `${pulledReport?.propertyDetails?.bedrooms} bd`,
      }),
      ...(pulledReport?.propertyDetails?.totalBaths && {
        Bathrooms: `${pulledReport?.propertyDetails?.totalBaths} ba`,
      }),
      ...(pulledReport?.propertyDetails?.totalRooms && {
        "Total Rooms": pulledReport?.propertyDetails?.totalRooms,
      }),
      ...(pulledReport?.propertyDetails?.parkingSpaces && {
        Parking: `${pulledReport?.propertyDetails?.parkingSpaces} spaces`,
      }),
      ...(pulledReport?.propertyDetails?.parkingTypeCode &&
        pulledReport?.propertyDetails?.parkingTypeCode !== "Type Unknown" && {
          "Parking Type": pulledReport?.propertyDetails?.parkingTypeCode,
        }),
      ...(pulledReport?.propertyDetails?.qualityCode && {
        Condition: pulledReport?.propertyDetails?.qualityCode,
      }),
      ...(pulledReport?.propertyDetails?.yearBuilt && {
        "Year Built": pulledReport?.propertyDetails?.yearBuilt,
      }),
      ...(pulledReport?.propertyDetails?.stories && {
        Stories: parseInt(pulledReport?.propertyDetails?.stories).toString(),
      }),
    },
  ];

  return (
    <Padding
      left="md"
      right="md"
      breakpointStyles={{
        desktop: {
          space: "none",
        },
      }}
    >
      <Container
        containerTitle={`Lot & Building Details ${
          loading
            ? ""
            : `(${
                pulledReport?.propertyReportSummary?.lotAndBuildingDetailsCount
                  ? pulledReport.propertyReportSummary
                      ?.lotAndBuildingDetailsCount
                  : 0
              })`
        }`}
        containerId={containerId}
      >
        {loading ? (
          <>
            <Container altColor="grey">
              <Layout
                type="flex"
                flexProps={{ direction: "column-reverse", gap: "md" }}
                breakpointStyles={{
                  desktop: {
                    type: "grid",
                    gridProps: {
                      templateColumns: "2-column",
                      alignItems: "center",
                    },
                  },
                }}
              >
                <Padding
                  space="md"
                  top="none"
                  breakpointStyles={{
                    desktop: {
                      space: "md",
                    },
                  }}
                >
                  <Padding bottom="xs">
                    <LoadingPlaceholder width={"12rem"} height={"20px"} />
                  </Padding>
                  <Padding bottom="xs">
                    <LoadingPlaceholder width={"12rem"} height={"20px"} />
                  </Padding>
                  <Padding bottom="xs">
                    <LoadingPlaceholder width={"12rem"} height={"20px"} />
                  </Padding>
                  <Padding bottom="xs">
                    <LoadingPlaceholder width={"12rem"} height={"20px"} />
                  </Padding>
                </Padding>
                <Padding space="md">
                  <LoadingPlaceholder width={"100%"} height={200} />
                </Padding>
              </Layout>
            </Container>
            <LoadingContainerLineItem
              lineItemHeader
              numberToLoad={3}
              children
            />
          </>
        ) : pulledReport?.propertyReportSummary?.lotAndBuildingDetailsCount &&
          pulledReport.propertyReportSummary?.lotAndBuildingDetailsCount > 0 ? (
          <>
            <Container altColor="grey">
              <Padding space="md">
                <Layout
                  type="flex"
                  flexProps={{ direction: "column-reverse", gap: "md" }}
                  breakpointStyles={{
                    desktop: {
                      type: "grid",
                      gridProps: {
                        templateColumns: "2-column",
                        alignItems: "center",
                      },
                    },
                  }}
                >
                  <Layout type="flex" flexProps={{ direction: "column" }}>
                    {pulledReport.preforeclosureDetails ? (
                      <Padding bottom="xs">
                        <Text fontWeight="bold" color="error">
                          Pre-Foreclosure
                        </Text>
                      </Padding>
                    ) : null}
                    {pulledReport.propertyReportSummary
                      ?.propertyIndicatorOverview ? (
                      <Padding bottom="xs">
                        <Layout
                          type="flex"
                          flexProps={{
                            direction: "row",
                            gap: "xxs",
                            alignItems: "center",
                          }}
                        >
                          <Icon size="24" name={IconList.IconHouse} />
                          <Text size="sm">
                            {
                              pulledReport.propertyReportSummary
                                ?.propertyIndicatorOverview
                            }
                          </Text>
                        </Layout>
                      </Padding>
                    ) : null}
                    {pulledReport.propertyDetails?.yearBuilt ? (
                      <Padding bottom="xs">
                        <Layout
                          type="flex"
                          flexProps={{
                            direction: "row",
                            gap: "xxs",
                            alignItems: "center",
                          }}
                        >
                          <Icon size="24" name={IconList.IconHammer} />
                          <Text size="sm">
                            {"Built in " +
                              pulledReport.propertyDetails?.yearBuilt}
                          </Text>
                        </Layout>
                      </Padding>
                    ) : null}
                    {pulledReport.propertyDetails?.buildingSquareFeet ? (
                      <Padding bottom="xs">
                        <Layout
                          type="flex"
                          flexProps={{
                            direction: "row",
                            gap: "xxs",
                            alignItems: "center",
                          }}
                        >
                          <Icon size="24" name={IconList.IconRuler} />

                          <Text size="sm">
                            {pulledReport.propertyDetails?.buildingSquareFeet +
                              " sq ft Building"}
                          </Text>
                        </Layout>
                      </Padding>
                    ) : null}
                    {pulledReport.propertyDetails?.landSquareFootage ? (
                      <Padding bottom="xs">
                        <Layout
                          type="flex"
                          flexProps={{
                            direction: "row",
                            gap: "xxs",
                            alignItems: "center",
                          }}
                        >
                          <Icon size="24" name={IconList.IconFence} />

                          <Text size="sm">
                            {pulledReport.propertyDetails?.acres &&
                            pulledReport.propertyDetails?.acres > 0.25
                              ? pulledReport.propertyDetails.acres + " Acres"
                              : pulledReport.propertyDetails?.landSquareFootage
                                ? pulledReport.propertyDetails
                                    ?.landSquareFootage + " sq ft Lot"
                                : null}
                          </Text>
                        </Layout>
                      </Padding>
                    ) : null}
                    {pulledReport.propertyReportSummary?.bedBathroomOverview ? (
                      <Padding bottom="xs">
                        <Layout
                          type="flex"
                          flexProps={{
                            direction: "row",
                            gap: "xxs",
                            alignItems: "center",
                          }}
                        >
                          {pulledReport.propertyDetails?.bedrooms ? (
                            <Layout
                              type="flex"
                              flexProps={{
                                direction: "row",
                                gap: "xxs",
                                alignItems: "center",
                              }}
                            >
                              <Icon size="24" name={IconList.IconBed} />

                              <Text size="sm">
                                {pulledReport.propertyDetails?.bedrooms} Beds
                              </Text>
                            </Layout>
                          ) : null}
                          {pulledReport.propertyDetails?.bedrooms &&
                          pulledReport.propertyDetails?.totalBaths ? (
                            <Text> | </Text>
                          ) : null}

                          {pulledReport.propertyDetails?.totalBaths ? (
                            <Layout
                              type="flex"
                              flexProps={{
                                direction: "row",
                                gap: "xxs",
                                alignItems: "center",
                              }}
                            >
                              <Icon size="24" name={IconList.IconBath} />
                              <Text>
                                {pulledReport.propertyDetails?.totalBaths} Bath
                              </Text>
                            </Layout>
                          ) : null}
                        </Layout>
                      </Padding>
                    ) : null}
                    {pulledReport.propertyDetails?.parkingTypeCode ||
                    pulledReport.propertyDetails?.parkingSpaces ? (
                      <Padding bottom="xs">
                        <Layout
                          type="flex"
                          flexProps={{
                            direction: "row",
                            gap: "xxs",
                            alignItems: "center",
                          }}
                        >
                          <Icon size="24" name={IconList.IconParking} />
                          <Text size="sm">
                            {generateList(
                              [
                                {
                                  value: pulledReport.propertyDetails
                                    ?.parkingTypeCode
                                    ? pulledReport.propertyDetails
                                        ?.parkingTypeCode
                                    : null,
                                  label: "",
                                },
                                {
                                  value: pulledReport.propertyDetails
                                    ?.parkingSpaces
                                    ? `${pulledReport.propertyDetails?.parkingSpaces} Spaces`
                                    : null,
                                  label: "",
                                },
                              ],
                              " | ",
                            )}
                          </Text>
                        </Layout>
                      </Padding>
                    ) : null}
                    {pulledReport.propertyDetails?.apn ? (
                      <Layout
                        type="flex"
                        flexProps={{
                          direction: "row",
                          gap: "xxs",
                          alignItems: "center",
                        }}
                      >
                        <Icon size="24" name={IconList.IconFolder} />
                        <Text size="sm">
                          Assessors Parcel Number:{" "}
                          {pulledReport.propertyDetails?.apn ?? null}
                        </Text>
                      </Layout>
                    ) : null}
                  </Layout>
                  {loading ? (
                    <LoadingPlaceholder width={"100%"} height={250} />
                  ) : (
                    <StreetView
                      lat={pulledReport?.propertyDetails?.latitude}
                      long={pulledReport?.propertyDetails?.longitude}
                      size={{ width: 374, height: 250 }}
                      staticMapProperties={{
                        markerColor: brandInfo.markerColor,
                        markerSize: "small",
                      }}
                      staticMapFlag={
                        pulledReport &&
                        pulledReport.propertyDetails &&
                        pulledReport.propertyDetails.acres &&
                        pulledReport.propertyDetails.acres > 5
                          ? true
                          : false
                      }
                    ></StreetView>
                  )}
                </Layout>
              </Padding>
            </Container>
            <CollapsableItemList>
              <ContainerLineItem
                lineItemHeader={`Key Property Insights`}
                key={`Key Property Insights`}
                isOpen={true}
              >
                <Padding space="md">
                  <Table
                    orderOfProperties={[]}
                    tableDataObject={highlightObjects}
                  />
                </Padding>
              </ContainerLineItem>
              {Object.keys(lotObjects).length > 0
                ? lotObjects.map((obj, index) => {
                    return (
                      <ContainerLineItem
                        lineItemHeader={`Lot Details`}
                        key={`${obj["Assessors Parcel Number"]}-${index}`}
                        isOpen={printReady ? true : false}
                      >
                        <Padding space="md">
                          <Table
                            orderOfProperties={[]}
                            tableDataObject={lotObjects[index]}
                          />
                        </Padding>
                      </ContainerLineItem>
                    );
                  })
                : null}
              {Object.keys(buildingObjects).length > 0
                ? buildingObjects.map((obj, index) => {
                    return (
                      <ContainerLineItem
                        lineItemHeader={`Building Details`}
                        key={`${obj["Total Square Footage"]}-${index}`}
                        isOpen={printReady ? true : false}
                      >
                        <Padding space="md">
                          <Table
                            orderOfProperties={[]}
                            tableDataObject={buildingObjects[index]}
                          />
                        </Padding>
                      </ContainerLineItem>
                    );
                  })
                : null}
            </CollapsableItemList>
          </>
        ) : (
          <Padding space="md">
            <Text size="sm" tag="b">
              No results found for Lot & Building Details
            </Text>
          </Padding>
        )}
      </Container>
    </Padding>
  );
};

export default LotBuildingDetails;
